import { z } from 'zod'
import { refOn } from '@omnea-app/packages/universal/references/refKinds'
import type { LeakClass } from '@omnea-app/packages/universal/aiContext/leakClass'

// Re-exported so slice code keeps importing LeakClass from the catalogue; the shared vocabulary lives in aiContext.
export type { LeakClass }

// Order within a scope drives the settings-UI checkbox order (ContextSlicesSection). Prompt-assembly order is
// separate (the registry SLICES array), so this is display-only.
export const SLICE_NAMES = [
  'request_documents',
  'other_request_forms',
  'review_history',
  'agreements',
  'supplier_documents',
  'prior_submissions',
  'precedents',
  'policies',
  'web_research',
] as const
export type SliceName = (typeof SLICE_NAMES)[number]
export const SliceNameSchema = z.enum(SLICE_NAMES)
export const isSliceName = (value: string): value is SliceName => SliceNameSchema.safeParse(value).success

/** Slices offered on the fill-in agent. `precedents` is review-outcome shaped (an approved/rejected
 * analogy over past reviews) and doesn't apply to form completion, so it's excluded pending a
 * fill-oriented redesign. */
export const FILL_IN_SLICE_NAMES = SLICE_NAMES.filter((name) => name !== 'precedents')

// Its own name so compose can diverge from fill-in without a rename at every call site.
export const CONTENT_GENERATION_SLICE_NAMES = FILL_IN_SLICE_NAMES

/** Scope display order, shared by in-prompt assembly and the config UI. Each scope is a heading grouping
 * slices; the unscoped policies slice carries no scope (it renders into the system prompt). */
export const SLICE_SCOPE_ORDER = ['organisation', 'this_request', 'this_supplier', 'similar_reviews', 'web'] as const
export type SliceScope = (typeof SLICE_SCOPE_ORDER)[number]

type SliceFlagKey =
  | 'RELEASE_APPROVAL_AGENT_OTHER_REQUEST_FORMS'
  | 'RELEASE_APPROVAL_AGENT_WEB_RESEARCH'
  | 'RELEASE_APPROVAL_AGENT_REVIEW_HISTORY'

/** `flagKey` absent ⇒ the slice has GA'd and is always available. */
export type SliceCatalogueEntry = {
  /** Omitted for unscoped slices (policies): they render into the system prompt, not under a scope heading. */
  scope?: SliceScope
  leakClass: LeakClass
  flagKey?: SliceFlagKey
}

/** Per-step config for one slice. On/off only today; a slice that needs more (e.g. a migrated `policies`
 * slice carrying its `policyIds`) extends its own entry below — additive, with no format migration,
 * because the persisted shape is a per-slice object keyed by name rather than a flat name list. */
export const BaseSliceConfigSchema = z.object({ enabled: z.boolean() })
export type SliceConfig = z.infer<typeof BaseSliceConfigSchema>

/** Policies carries its document list in slice config like every other slice, rather than in a bespoke
 * top-level `agentConfig.policyIds` field. */
export const PoliciesSliceConfigSchema = z.object({ ...BaseSliceConfigSchema.shape, policyIds: z.array(z.uuid()).optional() })

/** The earlier form submissions on this request the agent should see, selected as the form templates of
 * form-fill tasks on blocks earlier in the workflow. */
export const OtherRequestFormsSliceConfigSchema = z.object({
  ...BaseSliceConfigSchema.shape,
  formTemplateIds: z.array(refOn(z.uuid(), 'formTemplate')).optional(),
})

/** Optional domain allowlist for web research; absent or empty = open. Plain strings, because nothing
 * parses this schema at runtime — a validating transform here would imply a write-time guarantee that does
 * not exist. The builder validates on entry and checkUrl re-checks every entry it reads. */
export const WebResearchSliceConfigSchema = z.object({
  ...BaseSliceConfigSchema.shape,
  allowedDomains: z.array(z.string()).optional(),
})
export type WebResearchSliceConfig = z.infer<typeof WebResearchSliceConfigSchema>

const SLICE_CONFIG_SCHEMAS = {
  request_documents: BaseSliceConfigSchema,
  other_request_forms: OtherRequestFormsSliceConfigSchema,
  review_history: BaseSliceConfigSchema,
  agreements: BaseSliceConfigSchema,
  prior_submissions: BaseSliceConfigSchema,
  supplier_documents: BaseSliceConfigSchema,
  precedents: BaseSliceConfigSchema,
  policies: PoliciesSliceConfigSchema,
  web_research: WebResearchSliceConfigSchema,
} satisfies Record<SliceName, z.ZodTypeAny>

/** Write-side: closed key set (`.strict`), so a typo'd slice name fails at parse time. */
export const SlicesConfigSchema = z.object(SLICE_CONFIG_SCHEMAS).partial().strict()
export type SlicesConfig = z.infer<typeof SlicesConfigSchema>

/** Read-side one-slice config entry: the union of every slice's read-side fields (id lists stay
 * string-only; a bad id fails loud later in the slice's loader). What a slice's `fetchUpfrontData`
 * receives for its own entry. */
export const SliceConfigReadSchema = z.object({
  ...BaseSliceConfigSchema.shape,
  policyIds: z.array(z.string()).optional(),
  formTemplateIds: z.array(z.string()).optional(),
  // Soft on read: keep strings as stored; checkUrl ignores entries that do not normalise.
  allowedDomains: z.array(z.string()).optional(),
})
export type SliceConfigRead = z.infer<typeof SliceConfigReadSchema>

/** Read-side: permissive string keys (a removed slice name is legitimate jsonb, not drift); unknown
 * names are ignored by `enabledSliceNamesFromConfig`. `policyIds` is carried through for the policies
 * slice; other slices simply never set it. */
export const SlicesConfigReadSchema = z.record(z.string(), SliceConfigReadSchema)

/** The known slice names a step has switched on, dropping unknown/removed names. */
export const enabledSliceNamesFromConfig = (slices: Record<string, SliceConfig | undefined> | undefined): SliceName[] =>
  SLICE_NAMES.filter((name) => slices?.[name]?.enabled === true)

/** Validate untrusted jsonb into a clean config map, keeping only known slices. */
export const coerceSlicesConfig = (raw: unknown): SlicesConfig => {
  const parsed = SlicesConfigReadSchema.safeParse(raw)
  const record = parsed.success ? parsed.data : {}
  const result: SlicesConfig = {}
  for (const name of SLICE_NAMES) {
    const entry = record[name]
    if (entry) result[name] = entry
  }
  return result
}

export const SLICE_CATALOGUE = {
  request_documents: {
    scope: 'this_request',
    leakClass: 'INTERNAL',
  },
  other_request_forms: {
    scope: 'this_request',
    leakClass: 'INTERNAL',
    flagKey: 'RELEASE_APPROVAL_AGENT_OTHER_REQUEST_FORMS',
  },
  // INTERNAL, unlike precedents: everything here already sits on the request in front of the reader, so
  // the slice widens nothing an approver could not open in the timeline themselves.
  review_history: {
    scope: 'this_request',
    leakClass: 'INTERNAL',
    flagKey: 'RELEASE_APPROVAL_AGENT_REVIEW_HISTORY',
  },
  agreements: {
    scope: 'this_supplier',
    leakClass: 'RESTRICTED',
  },
  supplier_documents: {
    scope: 'this_supplier',
    leakClass: 'INTERNAL',
  },
  prior_submissions: {
    scope: 'this_supplier',
    leakClass: 'RESTRICTED',
  },
  precedents: {
    scope: 'similar_reviews',
    leakClass: 'RESTRICTED',
  },
  policies: {
    scope: 'organisation',
    leakClass: 'INTERNAL',
    flagKey: undefined,
  },
  // PUBLIC: the content pulled *in* is public web pages. The outbound concern — search queries and scraped
  // URLs derived from request data leaving the tenant to OpenAI/Firecrawl — isn't a leak class; it's surfaced
  // as a warning under the config checkbox (ContextSlicesSection).
  web_research: {
    scope: 'web',
    leakClass: 'PUBLIC',
    flagKey: 'RELEASE_APPROVAL_AGENT_WEB_RESEARCH',
  },
} as const satisfies Record<SliceName, SliceCatalogueEntry>

/** Release flag gating a slice, or `undefined` once GA'd. The `SliceCatalogueEntry` annotation is
 * load-bearing: the `as const` union has members without `flagKey`, so reading it straight off
 * `SLICE_CATALOGUE[name]` would not type-check. */
export const sliceFlagKey = (name: SliceName): SliceFlagKey | undefined => {
  const entry: SliceCatalogueEntry = SLICE_CATALOGUE[name]
  return entry.flagKey
}
